import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { User } from "lucide-react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useApi } from "@/lib/api";
import { useCopy, sortByOrder } from "@/lib/copy";
import { SectionHeading, Eyebrow, EmptyState, LoadingState, ArrowLink } from "@/components/site/Content";
import { SmartImage, Swappable, HeroBackdrop } from "@/components/site/Media";
import { Rich } from "@/components/site/Rich";
import { EcosystemGraphic } from "@/components/site/EcosystemGraphic";
import { HeroNetwork } from "@/components/site/Brand";

const TeamList = ({ t }) => {
  const { data, loading } = useApi("/team");
  if (loading) return <LoadingState className="h-40" label="Loading team" />;
  const team = data?.items || [];
  if (!team.length) return <EmptyState title={t("about.team.emptyTitle")} message={t("about.team.emptyText")} testId="about-team-empty" />;
  return (
    <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {team.map((m) => (
        <li key={m.id} className="border border-line p-6" data-testid={`about-team-member-${m.id}`}>
          {m.imageUrl ? (
            <SmartImage src={m.imageUrl.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${m.imageUrl}` : m.imageUrl} alt={`Portrait of ${m.name}`} ratio="aspect-square" />
          ) : (
            <div className="flex aspect-square items-center justify-center bg-pale text-brand" role="img" aria-label={`No photo available for ${m.name}`}><User className="h-10 w-10" aria-hidden="true" /></div>
          )}
          <p className="mt-5 font-bold text-ink">{m.name}</p>
          {m.shortBiography && <p className="mt-3 text-sm leading-relaxed text-ink">{m.shortBiography}</p>}
          {m.professionalProfileUrl && <a href={m.professionalProfileUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm font-bold text-brand underline">Professional profile</a>}
        </li>
      ))}
    </ul>
  );
};

const Statement = ({ label, children, testId }) => (
  <div className="border-t-2 border-brand pt-6" data-testid={testId}>
    <p className="eyebrow text-brand-green-dark">{label}</p>
    <p className="mt-4 whitespace-pre-line text-lg font-semibold leading-relaxed text-ink md:text-xl">{children}</p>
  </div>
);

const Step = ({ n, label, title, text, children, tone = "white", testId }) => (
  <section className={`${tone === "mist" ? "bg-mist" : "bg-white"} border-t border-line`} aria-labelledby={`${testId}-title`} data-testid={testId}>
    <div className="container-x grid gap-8 py-16 md:py-24 lg:grid-cols-12 lg:gap-12">
      <div className="lg:col-span-3">
        <p className="text-6xl font-extrabold leading-none tracking-tight text-brand-green-dark md:text-7xl" aria-hidden="true">{n}</p>
        <p className="eyebrow mt-4 text-brand">{label}</p>
      </div>
      <div className="lg:col-span-9">
        <h2 id={`${testId}-title`} className="max-w-3xl text-3xl font-extrabold leading-[1.1] tracking-tight text-brand sm:text-4xl lg:text-5xl">{title}</h2>
        {text && <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg"><Rich text={text} /></p>}
        {children && <div className="mt-12">{children}</div>}
      </div>
    </div>
  </section>
);

const Figures = ({ t }) => (
  <dl className="grid gap-px bg-line sm:grid-cols-3" data-testid="about-figures">
    {[1, 2, 3].filter((i) => t(`about.m1.stat${i}.value`)).map((i) => (
      <div key={i} className="bg-white p-6 md:p-8">
        <dt className="text-4xl font-extrabold tracking-tight text-brand md:text-5xl">{t(`about.m1.stat${i}.value`)}</dt>
        <dd className="mt-3 text-sm leading-relaxed text-ink-muted">{t(`about.m1.stat${i}.label`)}</dd>
      </div>
    ))}
  </dl>
);

const Gaps = ({ t }) => (
  <ol className="grid gap-8 md:grid-cols-3" data-testid="about-gaps">
    {[1, 2, 3].map((i) => (
      <li key={i} className="border-l-4 border-brand pl-6">
        <h3 className="text-lg font-extrabold text-brand">{t(`about.m2.gap${i}.title`)}</h3>
        {t(`about.m2.gap${i}.text`) && <p className="mt-2 text-sm leading-relaxed text-ink-muted">{t(`about.m2.gap${i}.text`)}</p>}
      </li>
    ))}
  </ol>
);

const Exists = ({ t }) => (
  <div className="grid items-center gap-10 xl:grid-cols-2">
    <div className="space-y-10">
      <Statement label={t("about.vision.label")} testId="about-vision">{t("about.vision.text")}</Statement>
      <Statement label={t("about.mission.label")} testId="about-mission">{t("about.mission.text")}</Statement>
    </div>
    <Swappable src={t("about.ecosystem.image")} alt={t("about.ecosystem.imageAlt")} className="mx-auto h-auto w-full max-w-[560px]"><EcosystemGraphic className="mx-auto h-auto w-full max-w-[560px]" /></Swappable>
  </div>
);

const SubHeading = ({ eyebrow, title, intro, id }) => (
  <div className="mb-8">
    {eyebrow && <p className="eyebrow text-brand-green-dark">{eyebrow}</p>}
    <h3 id={id} className="mt-2 text-2xl font-extrabold tracking-tight text-brand">{title}</h3>
    {intro && <p className="mt-2 text-ink-muted">{intro}</p>}
  </div>
);

const Pillars = ({ t }) => (
  <div aria-labelledby="pillars-heading" data-testid="about-pillars">
    <SubHeading id="pillars-heading" eyebrow={t("about.pillars.eyebrow")} title={t("about.pillars.title")} intro={t("about.pillars.intro")} />
    <ol className="grid gap-px border border-line bg-line md:grid-cols-3">
      {sortByOrder(["about.pillar1", "about.pillar2", "about.pillar3"], t).map((k, i) => (
        <li key={k} className="bg-white p-8" data-testid={`about-pillar-${i + 1}`}>
          <span className="text-sm font-extrabold text-brand-green-dark">{String(i + 1).padStart(2, "0")}</span>
          <h4 className="mt-4 text-xl font-extrabold text-brand">{t(`${k}.title`)}</h4>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-muted">{t(`${k}.text`)}</p>
        </li>
      ))}
    </ol>
  </div>
);

const Approach = ({ t }) => (
  <div className="mt-16" aria-labelledby="approach-heading" data-testid="about-approach">
    <SubHeading id="approach-heading" eyebrow={t("about.approach.eyebrow")} title={t("about.approach.title")} intro={t("about.approach.intro")} />
    <ol className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
      {sortByOrder(["about.approach1", "about.approach2", "about.approach3", "about.approach4"], t).map((k, i) => (
        <li key={k} className="border-l-4 border-brand-green pl-6" data-testid={`about-approach-${i + 1}`}>
          <h4 className="text-2xl font-extrabold tracking-tight text-brand">{t(`${k}.title`)}</h4>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-muted">{t(`${k}.text`)}</p>
        </li>
      ))}
    </ol>
  </div>
);

const Opening = ({ t }) => (
  <section className="relative overflow-hidden bg-brand text-white" data-testid="about-manifesto-opening">
    <HeroBackdrop src={t("about.header.bgImage")}><HeroNetwork className="pointer-events-none absolute inset-0 h-full w-full opacity-50 md:opacity-100" /></HeroBackdrop>
    <div className="container-x relative py-20 md:py-32">
      {t("about.header.eyebrow") && <Eyebrow dark className="fade-up mb-6">{t("about.header.eyebrow")}</Eyebrow>}
      <h1 className="fade-up max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }} data-testid="page-title">{t("about.header.title")}</h1>
      <p className="fade-up mt-10 max-w-3xl border-l-4 border-brand-green pl-6 text-xl font-semibold leading-snug text-white sm:text-2xl lg:text-[1.75rem]" style={{ animationDelay: "160ms" }} data-testid="about-intro"><Rich text={t("about.header.intro")} /></p>
    </div>
  </section>
);

const InitiativeCard = ({ image, alt, eyebrow, title, text, links, testId }) => (
  <article className="flex flex-col overflow-hidden bg-pale" data-testid={testId}>
    <SmartImage src={image} alt={alt} ratio="aspect-video" />
    <div className="flex flex-1 flex-col justify-between gap-8 p-8 md:p-10">
      <div>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h3 className="mt-3 text-2xl font-extrabold text-brand sm:text-3xl">{title}</h3>
        <p className="mt-4 leading-relaxed text-ink" data-testid={`${testId}-text`}><Rich text={text} /></p>
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-3">
        {links.map((l) => <ArrowLink key={l.to} to={l.to} testId={l.testId}>{l.label}</ArrowLink>)}
      </div>
    </div>
  </article>
);

const Initiatives = ({ t }) => (
  <section id="missions" className="scroll-mt-24 border-t border-line" aria-labelledby="initiatives-heading" data-testid="about-initiatives">
    <div className="container-x py-16 md:py-24">
      <SectionHeading id="initiatives-heading" eyebrow={t("about.initiatives.eyebrow")} title={t("about.initiatives.title")} />
      <div className="grid gap-6 md:grid-cols-2">
        <InitiativeCard testId="about-water-mission" image={t("about.water.image")} alt={t("about.water.imageAlt")} eyebrow={t("about.water.eyebrow")} title={t("about.water.title")} text={t("about.water.text")}
          links={[{ to: "/water-mission", label: t("about.water.link"), testId: "about-water-mission-link" }]} />
        <InitiativeCard testId="about-prasara-card" image={t("about.prasaraCard.image")} alt={t("about.prasaraCard.imageAlt")} eyebrow={t("about.prasaraCard.eyebrow")} title={t("about.prasaraCard.title")} text={t("about.prasaraCard.text")}
          links={[{ to: "/research", label: t("about.prasaraCard.storiesLink"), testId: "about-prasara-stories-link" }, { to: "/events", label: t("about.prasaraCard.eventsLink"), testId: "about-prasara-events-link" }]} />
      </div>
    </div>
  </section>
);

export default function About() {
  const t = useCopy();
  usePageMeta("About BRaIN", "Why BRaIN exists: the case for connecting Bengaluru's research and innovation ecosystem.");
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.querySelector(hash)?.scrollIntoView();
  }, [hash]);

  return (
    <>
      <Opening t={t} />
      <Step n="01" label={t("about.m1.label")} title={t("about.m1.title")} text={t("about.m1.text")} testId="about-step-1"><Figures t={t} /></Step>
      <Step n="02" label={t("about.m2.label")} title={t("about.m2.title")} text={t("about.m2.text")} tone="mist" testId="about-step-2"><Gaps t={t} /></Step>
      <Step n="03" label={t("about.m3.label")} title={t("about.m3.title")} text={t("about.m3.text")} testId="about-step-3" />
      <Step n="04" label={t("about.m4.label")} title={t("about.m4.title")} tone="mist" testId="about-step-4"><Exists t={t} /></Step>
      <Step n="05" label={t("about.m5.label")} title={t("about.m5.title")} testId="about-step-5"><Pillars t={t} /><Approach t={t} /></Step>

      <section className="border-t border-line bg-mist" aria-labelledby="team-heading">
        <div className="container-x py-16 md:py-24">
          <SectionHeading id="team-heading" eyebrow={t("about.team.eyebrow")} title={t("about.team.title")} />
          <TeamList t={t} />
        </div>
      </section>

      <Initiatives t={t} />
    </>
  );
}
